import { useEffect, useMemo, useRef, useCallback } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { useNotifications } from '../context/NotificationsContext';
import { notificationsService } from '../services/notificationsService';
import { formatRelativeTime } from '../utils/formatRelativeTime';

const NotificationsPage = () => {
  const { notifications, loading, refresh, unreadCount, error, markViewed } = useNotifications();
  const pendingViewIds = useRef<Set<number>>(new Set());
  const observerRef = useRef<IntersectionObserver | null>(null);
  const nodeMapRef = useRef<Map<number, HTMLElement>>(new Map());

  const unreadIds = useMemo(() => new Set(notifications.filter((item) => !item.viewed_at).map((item) => item.id)), [notifications]);

  const registerObserver = useCallback(
    (node: HTMLElement | null, notificationId: number) => {
      const existing = nodeMapRef.current.get(notificationId);
      if (existing && observerRef.current) {
        observerRef.current.unobserve(existing);
      }

      if (!node) {
        nodeMapRef.current.delete(notificationId);
        return;
      }

      nodeMapRef.current.set(notificationId, node);
      if (observerRef.current && unreadIds.has(notificationId)) {
        observerRef.current.observe(node);
      }
    },
    [unreadIds]
  );

  useEffect(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        const toMark: number[] = [];
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const idAttr = (entry.target as HTMLElement).dataset.notificationId;
          const id = idAttr ? Number(idAttr) : null;
          if (!id || !unreadIds.has(id)) return;
          if (pendingViewIds.current.has(id)) return;
          pendingViewIds.current.add(id);
          toMark.push(id);
        });

        if (toMark.length) {
          markViewed(toMark);
        }
      },
      { threshold: 0.35 }
    );

    nodeMapRef.current.forEach((node, id) => {
      if (unreadIds.has(id)) {
        observerRef.current?.observe(node);
      }
    });

    return () => {
      observerRef.current?.disconnect();
      observerRef.current = null;
    };
  }, [markViewed, unreadIds]);

  return (
    <DashboardLayout title="Notifications">
      <div className="section">
        <div className="card hero-card notifications-hero">
          <div>
            <span className="eyebrow">Messages</span>
            <h3 style={{ margin: '0.4rem 0' }}>Your Gate.haus notifications</h3>
            <p style={{ color: 'var(--dark)' }}>
              Stay on top of security changes, identity updates, and app requests. Unread: <strong>{unreadCount}</strong>.
            </p>
          </div>
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
            <button type="button" onClick={refresh} disabled={loading}>
              {loading ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>
        </div>
        <div className="notifications-list">
          {error && (
            <div className="card notifications-empty">
              <h4>Couldn’t load notifications</h4>
              <p style={{ color: 'var(--gray)', margin: 0 }}>{error}</p>
            </div>
          )}
          {!error && notifications.length === 0 && !loading ? (
            <div className="card notifications-empty">
              <h4>All caught up</h4>
              <p style={{ color: 'var(--gray)', margin: 0 }}>New notifications will appear here as they arrive.</p>
            </div>
          ) : (
            notifications.map((item) => (
              <article
                key={item.id}
                data-notification-id={item.id}
                ref={(node) => registerObserver(node, item.id)}
                className={`card notification-card ${item.viewed_at ? '' : 'unread'}`}
              >
                <div className="notification-header">
                  <div className="notification-brand">
                    {item.client_logo ? (
                      <img src={item.client_logo} alt={item.client_name || 'Client'} width={44} height={44} />
                    ) : (
                      <div className="notification-avatar">
                        {item.client_name ? item.client_name.charAt(0) : 'G'}
                      </div>
                    )}
                    <div>
                      <h4>{item.title}</h4>
                      <span>
                        {item.client_name || 'Gate.haus'} ·{' '}
                        <span title={new Date(item.sent_at).toLocaleString()}>
                          {formatRelativeTime(item.sent_at)}
                        </span>
                      </span>
                    </div>
                  </div>
                  {!item.viewed_at && <span className="notification-dot" aria-label="Unread" />}
                </div>
                <p>{item.body}</p>
                {item.ctas && item.ctas.length > 0 && (
                  <div className="notification-actions">
                    {item.ctas.map((cta, index) => (
                      <a
                        key={`${item.id}-${index}`}
                        href={notificationsService.safeLinkUrl(cta.url)}
                        className="notification-link"
                        target="_blank"
                        rel="noreferrer"
                      >
                        {cta.label}
                      </a>
                    ))}
                  </div>
                )}
              </article>
            ))
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default NotificationsPage;
